import {
  Bell,
  ChevronDown,
  CircleUserRound,
  LogOut,
  MapPin,
  Radio,
  Search,
  Truck,
} from "lucide-react";
import { useMemo, useState } from "react";
import { clearToken } from "../api";
import type { Vehicle } from "../types";
import { BrandMark } from "./BrandMark";

const menus = [
  { label: "Operación", items: ["Centro C5", "Viajes activos", "Despacho"] },
  { label: "Flotilla", items: ["Unidades", "Remolques", "Cadena fría", "Conductores"] },
  { label: "Inteligencia", items: ["Expedientes", "Alertas", "Anomalías", "SLA", "Seguridad", "Evidencia", "Riesgo", "Geocercas"] },
  { label: "Mantenimiento", items: ["Predictivo", "Diagnóstico", "Órdenes", "Refacciones", "Sensores"] },
  { label: "Administración", items: ["Usuarios", "Permisos", "Auditoría", "Integraciones", "Compras técnicas"] },
];

export type FleetFilter = "all" | "moving" | "parked_hq" | "critical" | "offline";

const fleetFilters: { value: FleetFilter; label: string }[] = [
  { value: "all", label: "TODAS" },
  { value: "moving", label: "EN RUTA" },
  { value: "parked_hq", label: "EN SEDE" },
  { value: "critical", label: "CRÍTICAS" },
  { value: "offline", label: "SIN SEÑAL" },
];

export function TopNav({
  onLogout,
  vehicles,
  selectedId,
  onVehicleSelect,
  activeFilter,
  onFilterChange,
  alertCount,
  canManageUsers,
  canManagePermissions,
  canViewAudit,
  onOpenUsers,
  onOpenDevices,
  onOpenMaintenance,
  onOpenWorkOrders,
  onOpenInventory,
  onOpenPredictive,
  onOpenEvidence,
  onOpenSecurity,
  onOpenTrailers,
  onOpenReefers,
  onOpenRisk,
  onOpenSla,
  onOpenAnomalies,
  onOpenIncidents,
  onOpenTrips,
  onOpenVehicles,
  onOpenDrivers,
  onOpenGeofences,
  onOpenPermissions,
  onOpenAudit,
  onOpenProcurement,
}: {
  onLogout: () => void;
  vehicles: Vehicle[];
  selectedId: number;
  onVehicleSelect: (vehicle: Vehicle) => void;
  activeFilter: FleetFilter;
  onFilterChange: (filter: FleetFilter) => void;
  alertCount: number;
  canManageUsers: boolean;
  canManagePermissions: boolean;
  canViewAudit: boolean;
  onOpenUsers: () => void;
  onOpenDevices: () => void;
  onOpenMaintenance: () => void;
  onOpenWorkOrders: () => void;
  onOpenInventory: () => void;
  onOpenPredictive: () => void;
  onOpenEvidence: () => void;
  onOpenSecurity: () => void;
  onOpenTrailers: () => void;
  onOpenReefers: () => void;
  onOpenRisk: () => void;
  onOpenSla: () => void;
  onOpenAnomalies: () => void;
  onOpenIncidents: () => void;
  onOpenTrips: () => void;
  onOpenVehicles: () => void;
  onOpenDrivers: () => void;
  onOpenGeofences: () => void;
  onOpenPermissions: () => void;
  onOpenAudit: () => void;
  onOpenProcurement: () => void;
}) {
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const results = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("es-MX");
    if (!normalized) return vehicles.slice(0, 6);
    return vehicles
      .filter((vehicle) =>
        [
          vehicle.unit_code,
          vehicle.driver_name,
          vehicle.route_name,
          vehicle.plate,
          vehicle.make,
          vehicle.model,
          vehicle.metadata_json.cargo,
        ]
          .filter(Boolean)
          .some((value) => value.toLocaleLowerCase("es-MX").includes(normalized)),
      )
      .slice(0, 8);
  }, [query, vehicles]);

  const logout = () => {
    clearToken();
    onLogout();
  };
  return (
    <header className="topnav">
      <BrandMark />
      <nav className="topnav__menus" aria-label="Navegación principal">
        {menus.map((menu) => (
          <div className="nav-menu" key={menu.label}>
            <button type="button">
              {menu.label} <ChevronDown size={13} />
            </button>
            <div className="nav-menu__dropdown">
              {menu.items.map((item) => (
                <button
                  type="button"
                  key={item}
                  disabled={
                    (item === "Usuarios" && !canManageUsers) ||
                    (item === "Permisos" && !canManagePermissions) ||
                    (item === "Auditoría" && !canViewAudit)
                  }
                  onClick={
                    item === "Usuarios"
                      ? onOpenUsers
                      : item === "Permisos"
                        ? onOpenPermissions
                      : item === "Auditoría"
                        ? onOpenAudit
                      : item === "Compras técnicas"
                        ? onOpenProcurement
                      : item === "Geocercas"
                        ? onOpenGeofences
                      : item === "Conductores"
                        ? onOpenDrivers
                      : item === "Unidades"
                        ? onOpenVehicles
                      : item === "Viajes activos" || item === "Despacho"
                        ? onOpenTrips
                      : item === "Expedientes"
                        ? onOpenIncidents
                      : item === "Anomalías"
                        ? onOpenAnomalies
                      : item === "SLA"
                        ? onOpenSla
                      : item === "Predictivo"
                        ? onOpenPredictive
                      : item === "Cadena fría"
                        ? onOpenReefers
                      : item === "Remolques"
                        ? onOpenTrailers
                      : item === "Seguridad"
                        ? onOpenSecurity
                      : item === "Riesgo"
                        ? onOpenRisk
                      : item === "Evidencia"
                        ? onOpenEvidence
                      : item === "Órdenes"
                        ? onOpenWorkOrders
                      : item === "Refacciones"
                        ? onOpenInventory
                      : item === "Diagnóstico"
                        ? onOpenMaintenance
                      : item === "Sensores" || item === "Integraciones"
                        ? onOpenDevices
                        : undefined
                  }
                  title={
                    item === "Usuarios" && !canManageUsers
                      ? "Requiere rol superadmin"
                      : item === "Permisos" && !canManagePermissions
                        ? "Requiere administración de permisos"
                      : item === "Auditoría" && !canViewAudit
                        ? "Requiere permiso de auditoría"
                        : undefined
                  }
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="topnav__tools">
        <div
          className="command-search-wrap"
          onFocus={() => setSearchOpen(true)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setSearchOpen(false);
          }}
        >
          <label className="command-search">
            <Search size={15} />
            <input
              aria-label="Buscar unidades"
              placeholder="Unidad, operador, ruta..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && results[0]) {
                  onVehicleSelect(results[0]);
                  setQuery(results[0].unit_code);
                  setSearchOpen(false);
                }
                if (event.key === "Escape") {
                  setQuery("");
                  setSearchOpen(false);
                  event.currentTarget.blur();
                }
              }}
            />
          </label>
          {searchOpen && (
            <div className="command-search__popover">
              <div className="command-search__filters" aria-label="Filtrar flotilla">
                {fleetFilters.map((filter) => (
                  <button
                    type="button"
                    className={activeFilter === filter.value ? "is-active" : ""}
                    onClick={() => onFilterChange(filter.value)}
                    key={filter.value}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              <div className="command-search__results" role="listbox" aria-label="Resultados de unidades">
                {results.map((vehicle) => (
                  <button
                    type="button"
                    role="option"
                    aria-selected={vehicle.id === selectedId}
                    className={vehicle.id === selectedId ? "is-selected" : ""}
                    onClick={() => {
                      onVehicleSelect(vehicle);
                      setQuery(vehicle.unit_code);
                      setSearchOpen(false);
                    }}
                    key={vehicle.id}
                  >
                    <span className={`status-dot is-${vehicle.status}`} />
                    <span>
                      <strong>{vehicle.unit_code}</strong>
                      <small>{vehicle.driver_name} · {vehicle.route_name}</small>
                    </span>
                    {vehicle.metadata_json.location_state === "parked_hq" ? <MapPin size={13} /> : <Truck size={13} />}
                  </button>
                ))}
                {!results.length && <p>SIN COINCIDENCIAS EN LA FLOTILLA</p>}
              </div>
            </div>
          )}
        </div>
        <span className="live-signal"><Radio size={14} /> EN VIVO</span>
        <button className="icon-button" aria-label={`${alertCount} alertas abiertas`} title={`${alertCount} alertas abiertas`}>
          <Bell size={18} />{alertCount > 0 && <i />}
        </button>
        <div className="nav-menu nav-menu--account">
          <button type="button"><CircleUserRound size={20} /><ChevronDown size={13} /></button>
          <div className="nav-menu__dropdown nav-menu__dropdown--right">
            <button type="button"><CircleUserRound size={14} /> Mi perfil</button>
            <button type="button" onClick={logout}><LogOut size={14} /> Cerrar sesión</button>
          </div>
        </div>
      </div>
    </header>
  );
}
